import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Plus, Gamepad2, Calendar, Ticket, Target, Trash2, MapPin, Settings, ScanLine, Search, X, LayoutGrid, List } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { DashboardHeader } from "@/components/DashboardHeader";
import { DashboardKpis } from "@/components/DashboardKpis";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";
import CreateContestDialog from "@/components/CreateContestDialog";
import DashboardStoresMap from "@/components/DashboardStoresMap";
import { ContestTable } from "@/components/ContestTable";
import Logo, { BrandFooter } from "@/components/Logo";

const statusStyle = {
  Attivo: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  Programmato: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  Concluso: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};

const fmt = (iso) =>
  new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const can = (p) => isAdmin || (user?.permissions || []).includes(p);
  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [counts, setCounts] = useState({ demo: 0, support: 0 });
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("end");
  const [view, setView] = useState(() => localStorage.getItem("nc_dash_view") || "cards");
  const setViewPersist = (v) => { setView(v); localStorage.setItem("nc_dash_view", v); };
  const SORTS = [["end", "Scadenza"], ["name", "Nome"], ["plays", "Giocate oggi"]];
  const sortFn = (a, b) => sortBy === "name" ? a.name.localeCompare(b.name, "it") : sortBy === "plays" ? (b.plays_today || 0) - (a.plays_today || 0) : new Date(a.end_datetime) - new Date(b.end_datetime);
  const STATUS_FILTERS = [["", "Tutti"], ["Attivo", "Attivi"], ["Programmato", "In programma"], ["Concluso", "Conclusi"]];
  const TYPE_FILTERS = [["", "Tutti"], ["ocr", "Scontrino"], ["whatsapp", "WhatsApp"], ["totem", "Totem"], ["web", "Online"], ["partner", "App terzi"]];
  const isType = (c, f) => !f || (f === "ocr" ? c.receipt_ocr_enabled : f === "whatsapp" ? (c.whatsapp_enabled || c.channel === "whatsapp") : f === "totem" ? c.totem_enabled !== false && c.channel !== "whatsapp" : f === "web" ? c.web_enabled : c.partner_enabled);
  const isStatus = (c, s) => !s || c.status === s;
  const q = search.trim().toLowerCase();
  const matchQ = (c) => !q || c.name.toLowerCase().includes(q) || (c.description || "").toLowerCase().includes(q);
  const visible = contests.filter((c) => isType(c, typeFilter) && isStatus(c, statusFilter) && matchQ(c)).sort(sortFn);
  useEffect(() => {
    if (!isAdmin) return;
    const tick = () => api.inboxCounts().then(setCounts).catch(() => {});
    tick();
    const t = setInterval(tick, 60000);
    return () => clearInterval(t);
  }, [isAdmin]);

  const load = async () => {
    try {
      setContests(await api.listContests());
    } catch (e) {
      toast.error("Errore nel caricamento dei concorsi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Eliminare questo concorso e tutti i suoi dati?")) return;
    await api.deleteContest(id);
    toast.success("Concorso eliminato");
    load();
  };

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader user={user} isAdmin={isAdmin} can={can} counts={counts} logout={logout} onCreate={() => setOpen(true)} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}</p>
            <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mt-1">Ciao{user?.name ? `, ${user.name.split(" ")[0]}` : ""} 👋</h2>
            <p className="text-muted-foreground mt-1 text-sm">
              {isAdmin ? "Ecco come stanno andando i tuoi concorsi oggi." : "Accesso operatore: vedi solo le sezioni che ti sono state assegnate."}
            </p>
          </div>
        </div>
        {!loading && <DashboardKpis contests={contests} counts={counts} isAdmin={isAdmin} />}
        {!loading && contests.length > 0 && <h3 className="font-display font-bold text-lg sm:text-xl tracking-tight mb-3" data-testid="contests-heading">I tuoi concorsi <span className="text-muted-foreground font-normal text-base">({contests.length})</span></h3>}
        {loading && <p className="text-muted-foreground">Caricamento…</p>}
        {!loading && contests.length === 0 && (
          <Card className="p-12 text-center border-dashed border-2 bg-card/50" data-testid="empty-state">
            <Trophy className="h-12 w-12 mx-auto text-muted-foreground/40" />
            <h3 className="font-display font-bold text-xl mt-4">Nessun concorso ancora</h3>
            <p className="text-muted-foreground mt-1 mb-6">Crea il tuo primo concorso Instant Win per iniziare.</p>
            {can("contests_edit") && <Button data-testid="create-first-contest-button" onClick={() => setOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" /> Crea Nuovo Concorso
            </Button>}
          </Card>
        )}
        {!loading && contests.length > 0 && (
          <div className="rounded-2xl border border-border bg-card/60 p-3 mb-5 space-y-2" data-testid="contest-type-filter">
            <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[180px] max-w-xs">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca concorso…" className="h-8 w-full rounded-full border border-border bg-background pl-8 pr-7 text-xs focus:outline-none focus:ring-2 focus:ring-primary/30" data-testid="contest-search-input" />
              {search && <button type="button" onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Cancella ricerca" data-testid="contest-search-clear"><X className="h-3.5 w-3.5" /></button>}
            </div>
            <span className="text-xs text-muted-foreground mr-1 hidden sm:inline">Ordina:</span>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="h-8 rounded-full border border-border bg-background px-2.5 text-xs" data-testid="contest-sort-select">
              {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <div className="ml-auto flex rounded-full border border-border overflow-hidden" data-testid="contest-view-toggle">
              <button type="button" onClick={() => setViewPersist("cards")} aria-pressed={view === "cards"} title="Schede" data-testid="contest-view-cards" className={`px-2.5 py-1.5 text-xs flex items-center gap-1 ${view === "cards" ? "bg-primary/10 text-primary font-semibold" : "text-muted-foreground hover:text-foreground"}`}><LayoutGrid className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Schede</span></button>
              <button type="button" onClick={() => setViewPersist("table")} aria-pressed={view === "table"} title="Tabella compatta" data-testid="contest-view-table" className={`px-2.5 py-1.5 text-xs flex items-center gap-1 border-l border-border ${view === "table" ? "bg-primary/10 text-primary font-semibold" : "text-muted-foreground hover:text-foreground"}`}><List className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Tabella</span></button>
            </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs text-muted-foreground mr-1">Stato:</span>
            {STATUS_FILTERS.map(([v, l]) => { const cnt = contests.filter((c) => isStatus(c, v) && isType(c, typeFilter)).length; return (
              <button key={v} type="button" onClick={() => setStatusFilter(v)} data-testid={`contest-status-filter-${v || "all"}`} className={`rounded-full border px-3 py-1 text-xs transition-colors ${statusFilter === v ? "bg-primary/10 text-primary border-primary/40 font-semibold" : "border-border text-muted-foreground hover:text-foreground"}`}>{l} <span className="opacity-70">{cnt}</span></button>
            ); })}
            <span className="mx-2 h-4 w-px bg-border" />
            <span className="text-xs text-muted-foreground mr-1">Tipo:</span>
            {TYPE_FILTERS.map(([v, l]) => { const cnt = contests.filter((c) => isType(c, v) && isStatus(c, statusFilter)).length; return (
              <button key={v} type="button" onClick={() => setTypeFilter(v)} data-testid={`contest-type-filter-${v || "all"}`} className={`rounded-full border px-3 py-1 text-xs transition-colors ${typeFilter === v ? "bg-primary/10 text-primary border-primary/40 font-semibold" : "border-border text-muted-foreground hover:text-foreground"}`}>{l} <span className="opacity-70">{cnt}</span></button>
            ); })}
            </div>
          </div>
        )}
        {!loading && contests.length > 0 && visible.length === 0 && <p className="text-sm text-muted-foreground" data-testid="contest-type-filter-empty">Nessun concorso con questi filtri.</p>}
        {!loading && visible.length > 0 && view === "table" && <ContestTable contests={visible} fmt={fmt} statusStyle={statusStyle} />}
        {!loading && visible.length > 0 && view === "cards" && (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((c, i) => (
              <motion.div
                key={c.id}
                initial={CARD_INITIAL}
                animate={CARD_ANIMATE}
                transition={{ delay: i * 0.05 }}
              >
                <Card
                  data-testid={`contest-card-${c.id}`}
                  onClick={() => navigate(`/concorso/${c.id}`)}
                  className="p-5 bg-card border-border hover:border-primary/50 transition-colors cursor-pointer group"
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-display font-bold text-lg leading-tight group-hover:text-primary transition-colors">
                      {c.name}
                    </h3>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {c.receipt_ocr_enabled && <Badge className="border border-amber-300 bg-amber-50 text-amber-800 gap-1" title="Partecipazione con foto dello scontrino (OCR)" data-testid={`contest-ocr-badge-${c.id}`}><ScanLine className="h-3 w-3" /> Scontrino</Badge>}
                      <Badge className={`border ${statusStyle[c.status]}`}>{c.status}</Badge>
                    </div>
                  </div>
                  {c.description && (
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{c.description}</p>
                  )}

                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-3">
                    <Calendar className="h-3.5 w-3.5" />
                    {fmt(c.start_datetime)} → {fmt(c.end_datetime)}
                  </div>

                  <div className="grid grid-cols-3 gap-2 mt-4">
                    <Stat icon={Target} label="Giocate oggi" value={c.plays_today} />
                    <Stat icon={Trophy} label="Vincite oggi" value={c.wins_today} accent />
                    <Stat icon={Ticket} label="Tipi premio" value={c.prize_types} />
                    {c.wrong_store_today > 0 && <Stat icon={MapPin} label="Fuori sede oggi" value={c.wrong_store_today} accent />}
                  </div>

                  <div className="flex gap-2 mt-5">
                    <Button
                      data-testid={`manage-contest-${c.id}`}
                      variant="secondary"
                      className="flex-1 gap-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/concorso/${c.id}`);
                      }}
                    >
                      <Settings className="h-4 w-4" /> Gestisci
                    </Button>
                    {isAdmin && <Button
                      data-testid={`play-contest-${c.id}`}
                      disabled={c.channel === "whatsapp"}
                      className="flex-1 gap-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/gioca/${c.id}`);
                      }}
                    >
                      <Gamepad2 className="h-4 w-4" /> Gioca
                    </Button>}
                    {can("contests_edit") && <Button
                      data-testid={`delete-contest-${c.id}`}
                      variant="ghost"
                      size="icon"
                      onClick={(e) => remove(e, c.id)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>}
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
        {can("stores") && !loading && <section className="mt-10" data-testid="dashboard-map-section"><h3 className="font-display font-bold text-lg sm:text-xl tracking-tight mb-3">Sedi sul territorio</h3><DashboardStoresMap /></section>}
      </main>

      <CreateContestDialog open={open} onOpenChange={setOpen} onCreated={(created) => { load(); if (created?.receipt_ocr?.enabled) navigate(`/concorso/${created.id}?tab=scontrini`); }} />
      <BrandFooter className="mt-10" />
    </div>
  );
}

const CARD_INITIAL = { opacity: 0, y: 16 };
const CARD_ANIMATE = { opacity: 1, y: 0 };
const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-lg bg-secondary/50 px-2.5 py-2">
    <Icon className={`h-3.5 w-3.5 ${accent ? "text-amber-400" : "text-muted-foreground"}`} />
    <div className={`font-display font-bold text-lg mt-1 ${accent ? "text-amber-400" : ""}`}>{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
  </div>
);
